import { useEffect, useState } from 'react';
import Layout from '../components/layout';
import Title from '../components/title';
import { supabase } from '../supabase';
import './tablePage.css';
import searchIcon from '../assets/search.svg';
import editIcon from '../assets/edit.svg';
import deleteIcon from '../assets/delete.svg';

const CONTENT_PER_PAGE = 4;

const Content = () => {
  const [content, setContent] = useState([]);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sectionName, setSectionName] = useState('');
  const [textEn, setTextEn] = useState('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [id, setId] = useState('');
  const [editSectionName, setEditSectionName] = useState('');
  const [editTextEn, setEditTextEn] = useState('');
  const [isEditOpen, setIsEditOpen] = useState(false);

  const getContent = async () => {
    const res = await supabase.from('content_managment').select('*').order('id', { ascending: false });
    setContent(res.data || []);
  };

  useEffect(() => {
    getContent();
  }, []);

  const createContent = async (e) => {
    e.preventDefault();
    await supabase.from('content_managment').insert([{ section_name: sectionName, text_en: textEn }]);
    setSectionName('');
    setTextEn('');
    setIsCreateOpen(false);
    await getContent();
  };

  const editContent = (item) => {
    setId(item.id);
    setEditSectionName(item.section_name || '');
    setEditTextEn(item.text_en || '');
    setIsEditOpen(true);
  };

  const updateContent = async (e) => {
    e.preventDefault();
    await supabase
      .from('content_managment')
      .update({ section_name: editSectionName, text_en: editTextEn })
      .eq('id', id);
    setIsEditOpen(false);
    await getContent();
  };

  const deleteContent = async (contentId) => {
    await supabase.from('content_managment').delete().eq('id', contentId);
    await getContent();
  };

  const filteredContent = content.filter((item) => {
    const value = search.toLowerCase();
    return item.id.toString().includes(value) || item.section_name?.toLowerCase().includes(value) || item.text_en?.toLowerCase().includes(value);
  });

  const totalPages = Math.max(1, Math.ceil(filteredContent.length / CONTENT_PER_PAGE));
  const startIndex = (currentPage - 1) * CONTENT_PER_PAGE;
  const paginatedContent = filteredContent.slice(startIndex, startIndex + CONTENT_PER_PAGE);

  return (
    <Layout>
      <div className="table_page">
        <Title subtitle="Manage content" title="Content" />
        <div className="table_controls_card">
          <label className="table_search" htmlFor="content-search">
            <img alt="" src={searchIcon} />
            <input
              id="content-search"
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search content..."
              type="text"
              value={search}
            />
          </label>
          <div className="table_controls_actions">
            <span>{filteredContent.length} total records</span>
            <button className="table_primary_button" onClick={() => setIsCreateOpen(true)} type="button">
              Add Content
            </button>
          </div>
        </div>
        <div className="table_card">
          <div className="table_scroll">
            <table className="data_table">
              <thead><tr><th>ID</th><th>Section</th><th>Text</th><th>Actions</th></tr></thead>
              <tbody>
                {paginatedContent.map((item) => (
                  <tr key={item.id}>
                    <td>{item.id}</td>
                    <td>{item.section_name}</td>
                    <td>{item.text_en}</td>
                    <td>
                      <div className="table_actions">
                        <button
                          aria-label={`Edit content #${item.id}`}
                          className="table_icon_button"
                          onClick={() => editContent(item)}
                          type="button"
                        >
                          <img alt="" src={editIcon} />
                        </button>
                        <button
                          aria-label={`Delete content #${item.id}`}
                          className="table_icon_button table_icon_button_danger"
                          onClick={() => deleteContent(item.id)}
                          type="button"
                        >
                          <img alt="" src={deleteIcon} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="table_pagination">
            <button disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)} type="button">Previous</button>
            <span>Page {currentPage} of {totalPages}</span>
            <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(currentPage + 1)} type="button">Next</button>
          </div>
        </div>
        {isCreateOpen && (
          <div
            aria-labelledby="add-content-heading"
            aria-modal="true"
            className="table_modal_overlay"
            onClick={() => setIsCreateOpen(false)}
            role="dialog"
          >
            <div className="table_modal" onClick={(e) => e.stopPropagation()}>
              <div className="table_modal_header">
                <div>
                  <h2 id="add-content-heading">Add Content</h2>
                </div>
                <button
                  aria-label="Close add content form"
                  className="table_modal_close"
                  onClick={() => setIsCreateOpen(false)}
                  type="button"
                >
                  &#x2715;
                </button>
              </div>
              <form className="table_form" onSubmit={createContent}>
                <div className="table_form_grid">
                  <label className="table_form_field">
                    <span>Section Name</span>
                    <input onChange={(e) => setSectionName(e.target.value)} placeholder="Section name" value={sectionName} />
                  </label>
                  <label className="table_form_field table_form_field_full">
                    <span>Text</span>
                    <textarea onChange={(e) => setTextEn(e.target.value)} rows={3} value={textEn} />
                  </label>
                </div>
                <div className="table_form_actions">
                  <button className="table_secondary_button" onClick={() => setIsCreateOpen(false)} type="button">
                    Cancel
                  </button>
                  <button className="table_primary_button" type="submit">Create</button>
                </div>
              </form>
            </div>
          </div>
        )}
        {isEditOpen && (
          <div
            aria-labelledby="edit-content-heading"
            aria-modal="true"
            className="table_modal_overlay"
            onClick={() => setIsEditOpen(false)}
            role="dialog"
          >
            <div className="table_modal" onClick={(e) => e.stopPropagation()}>
              <div className="table_modal_header">
                <div>
                  <h2 id="edit-content-heading">Edit Content</h2>
                </div>
                <button
                  aria-label="Close edit content form"
                  className="table_modal_close"
                  onClick={() => setIsEditOpen(false)}
                  type="button"
                >
                  &#x2715;
                </button>
              </div>
              <form className="table_form" onSubmit={updateContent}>
                <div className="table_form_grid">
                  <label className="table_form_field">
                    <span>Section Name</span>
                    <input onChange={(e) => setEditSectionName(e.target.value)} value={editSectionName} />
                  </label>
                  <label className="table_form_field table_form_field_full">
                    <span>Text</span>
                    <textarea onChange={(e) => setEditTextEn(e.target.value)} rows={3} value={editTextEn} />
                  </label>
                </div>
                <div className="table_form_actions">
                  <button className="table_secondary_button" onClick={() => setIsEditOpen(false)} type="button">
                    Cancel
                  </button>
                  <button className="table_primary_button" type="submit">Save</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Content;
